import {useState, useRef} from "react";
import "./ExploreAppsComponent.scss";
import ExploreAppsCardComponent from "@/screens/app/explore-apps/explore-apps-card/ExploreAppsCardComponent.tsx";
import { Dialog, DialogContent } from "@/components/ui/dialog.tsx";
import AppPreviewDialogComponent from "@/screens/app/explore-apps/app-preview-dialog/AppPreviewDialogComponent.tsx";
import { Search, ChevronDown } from "lucide-react";

import appsData from './exploreapps.json';

interface App {
    id: number;
    image: string;
    url: string;
    name: string;
    description: string;
    author: string;
    technology: string;
    tags: string[];
}

const ExploreAppsComponent = () => {
    // State for managing dialog
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [selectedApp, setSelectedApp] = useState<App | null>(null);

    // Search and filter state
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All Apps");
    const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

    // Fixed at 10 cards max - no pagination needed
    const appsPerPage = 10;

    // Ref for the component container
    const componentRef = useRef<HTMLDivElement>(null);

    const apps: App[] = appsData.apps;

    // Generate categories dynamically from tags
    const allTags = apps.flatMap(app => app.tags || []);
    const uniqueTags = Array.from(new Set(allTags)).sort();
    const allCategories = ["All Apps", ...uniqueTags];
    
    // Show only first 6 categories initially, rest can be expanded
    const visibleCategories = isFiltersExpanded ? allCategories : allCategories.slice(0, 6);
    const hasMoreCategories = allCategories.length > 6;

    // Filter apps based on search and category
    const filteredApps = apps.filter(app => {
        const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            app.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === "All Apps" || 
                              (app.tags && app.tags.includes(selectedCategory));
        return matchesSearch && matchesCategory;
    });

    // Get first 10 apps (no pagination needed)
    const currentApps = filteredApps.slice(0, appsPerPage);

    // Handle app card click
    const handleAppClick = (appId: string) => {
        const app = apps.find(app => app.id === parseInt(appId));
        if (app) {
            setSelectedApp(app);
            setIsPreviewOpen(true);
        }
    };

    // Handle dialog close
    const handleClosePreview = () => {
        setIsPreviewOpen(false);
        // Optional: Clear selected app when dialog closes
        // setSelectedApp(null);
    };

    return (
        <>
            <div className="explore-apps-component">
                <div className="header">
                    <h1>Explore templates from the community</h1>
                    <p>Discover a curated collection of applications built by our community.</p>
                </div>

                {/* Search and Filter Bar */}
                <div className="search-filter-bar">
                    <div className="search-container">
                        <Search className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search apps..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <div className="filter-container">
                        <div className="category-filters">
                            {visibleCategories.map((category) => (
                                <button
                                    key={category}
                                    className={`category-filter ${selectedCategory === category ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(category)}
                                >
                                    {category}
                                </button>
                            ))}
                            
                            {hasMoreCategories && (
                                <button
                                    className="expand-filters-button"
                                    onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                                >
                                    {isFiltersExpanded ? 'Show Less' : `+${allCategories.length - 6} More`}
                                    <ChevronDown className={`expand-icon ${isFiltersExpanded ? 'expanded' : ''}`} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="apps-grid" ref={componentRef}>
                    {currentApps.map(app => (
                        <ExploreAppsCardComponent
                            key={app.id}
                            app={app}
                            onClick={(appId) => handleAppClick(appId)}
                        />
                    ))}
                </div>
            </div>

            <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
                <DialogContent className="app-preview-dialog-content">
                    <AppPreviewDialogComponent
                        app={selectedApp}
                        onClose={handleClosePreview}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ExploreAppsComponent;
